import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Wand2, Loader2, Save, Eye, 
  Plus, Trash2, GripVertical, BookOpen, Users, User,
  CheckCircle, Sparkles, Image, HelpCircle, Clock, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface LearningSlide {
  id: string;
  type: 'content' | 'quiz' | 'spot_hazard' | 'reflection';
  title: string;
  content: string;
  imagePrompt?: string;
  imageUrl?: string;
  generatingImage?: boolean;
  quizQuestion?: string;
  quizOptions?: string[];
  quizAnswer?: number;
  quizExplanation?: string;
}

interface Worker {
  id: string;
  display_name: string;
  team: string;
}

interface Cluster {
  id: string;
  cluster_name: string;
}

export default function CreateLearning() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillData = (location.state as any)?.prefill;
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Step 1: Describe training
  const [trainingDescription, setTrainingDescription] = useState('');
  
  // Step 2: Target selection
  const [targetType, setTargetType] = useState<'individual' | 'team' | 'cluster' | 'all'>('all');
  const [targetWorkerId, setTargetWorkerId] = useState('');
  const [targetTeam, setTargetTeam] = useState('');
  const [targetClusterId, setTargetClusterId] = useState('');

  // Step 3: Generated/edited content
  const [slides, setSlides] = useState<LearningSlide[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [xpReward, setXpReward] = useState(25);

  // Step 4: Timing
  const [deliveryTiming, setDeliveryTiming] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');

  // Data
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [companyDocuments, setCompanyDocuments] = useState<any[]>([]);

  // Prefill from MDP recommendation
  const [suggestionId, setSuggestionId] = useState<string | null>(null);
  const [prefillApplied, setPrefillApplied] = useState(false);

  const totalSteps = 4;
  const stepLabels = ['Describe', 'Target', 'Content', 'Timing'];

  useEffect(() => {
    loadData();
  }, []);

  // Apply prefill data from MDP recommendation
  useEffect(() => {
    if (prefillData && !prefillApplied) {
      if (prefillData.trainingDescription) setTrainingDescription(prefillData.trainingDescription);
      if (prefillData.title) setTitle(prefillData.title);
      if (prefillData.targetType) setTargetType(prefillData.targetType);
      if (prefillData.targetWorkerId) setTargetWorkerId(prefillData.targetWorkerId);
      if (prefillData.suggestionId) setSuggestionId(prefillData.suggestionId);
      setPrefillApplied(true);
    }
  }, [prefillData, prefillApplied]);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
      return;
    }

    const { data: profile } = await supabase
      .from('worker_profiles')
      .select('company_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profile?.company_id) {
      setCompanyId(profile.company_id);

      const { data: workersData } = await supabase
        .from('worker_profiles')
        .select('id, display_name, team')
        .eq('company_id', profile.company_id);
      setWorkers(workersData || []);

      const uniqueTeams = [...new Set((workersData || []).map(w => w.team))].filter(Boolean);
      setTeams(uniqueTeams);

      const { data: clustersData } = await supabase
        .from('worker_clusters')
        .select('id, cluster_name')
        .eq('company_id', profile.company_id);
      setClusters(clustersData || []);

      const { data: docsData } = await supabase
        .from('company_documents')
        .select('id, document_name, category, summary, extracted_text')
        .eq('company_id', profile.company_id)
        .eq('is_active', true)
        .limit(10);
      setCompanyDocuments(docsData || []);
    }
  };

  const handleGenerate = async () => {
    if (!trainingDescription.trim()) {
      toast.error('Please describe the training you want to create');
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-learning-content', {
        body: { 
          description: trainingDescription,
          targetType,
          companyId,
          companyDocuments: companyDocuments.map(doc => ({
            document_name: doc.document_name,
            category: doc.category,
            summary: doc.summary,
            extracted_text: doc.extracted_text?.substring(0, 2000)
          }))
        },
      });

      if (error) {
        // Check for AI limit exceeded
        if (error.message?.includes('limitExceeded') || error.message?.includes('AI_LIMIT_EXCEEDED')) {
          toast.error('Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.');
          return;
        }
        throw error;
      }

      // Also check response body for limit exceeded
      if (data?.limitExceeded) {
        toast.error('Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.');
        return;
      }

      if (data?.title) setTitle(data.title);
      if (data?.description) setDescription(data.description);
      if (data?.slides) setSlides(data.slides);
      if (data?.xpReward) setXpReward(data.xpReward);

      setStep(3);
      toast.success('Training content generated! Review and edit as needed.');
    } catch (error: any) {
      console.error('Error generating content:', error);
      
      // Handle 429 from context body
      if (error?.context?.body) {
        try {
          const body = typeof error.context.body === 'string' ? JSON.parse(error.context.body) : error.context.body;
          if (body.limitExceeded) {
            toast.error('Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.');
            return;
          }
        } catch {}
      }
      
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const generateImageForSlide = async (slideId: string) => {
    const slide = slides.find(s => s.id === slideId);
    if (!slide?.imagePrompt) {
      toast.error('No image prompt for this slide');
      return;
    }

    updateSlide(slideId, { generatingImage: true });

    try {
      const { data, error } = await supabase.functions.invoke('generate-slide-image', {
        body: { 
          imagePrompt: slide.imagePrompt,
          slideId,
          companyId
        },
      });

      if (error) throw error;
      if (data?.imageUrl) {
        updateSlide(slideId, { imageUrl: data.imageUrl, generatingImage: false });
        toast.success('Image generated!');
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      updateSlide(slideId, { generatingImage: false });
      toast.error(error.message || 'Failed to generate image');
    }
  };

  const generateAllImages = async () => {
    const slidesWithPrompts = slides.filter(s => s.imagePrompt && !s.imageUrl);
    if (slidesWithPrompts.length === 0) {
      toast.info('No slides need images');
      return;
    }

    setGeneratingImages(true);
    
    for (const slide of slidesWithPrompts) {
      await generateImageForSlide(slide.id);
      // Small delay between requests to avoid rate limiting
      await new Promise(r => setTimeout(r, 1000));
    }

    setGeneratingImages(false);
    toast.success(`Generated ${slidesWithPrompts.length} images!`);
  };

  const handlePublish = async () => {
    if (!title || slides.length === 0) {
      toast.error('Please add a title and at least one slide');
      return;
    }

    setLoading(true);
    try {
      let targetWorkerIds: string[] = [];

      if (targetType === 'individual' && targetWorkerId) {
        targetWorkerIds = [targetWorkerId];
      } else if (targetType === 'cluster' && targetClusterId) {
        const { data: cluster } = await supabase
          .from('worker_clusters')
          .select('member_ids')
          .eq('id', targetClusterId)
          .single();
        targetWorkerIds = cluster?.member_ids || [];
      } else if (targetType === 'team' && targetTeam) {
        const { data: teamWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId)
          .eq('team', targetTeam);
        targetWorkerIds = (teamWorkers || []).map(w => w.id);
      } else if (targetType === 'all') {
        const { data: allWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId);
        targetWorkerIds = (allWorkers || []).map(w => w.id);
      }

      if (targetWorkerIds.length === 0) {
        toast.error('No target workers found');
        setLoading(false);
        return;
      }

      // Create learning module
      const { data: moduleData, error: moduleError } = await supabase
        .from('learning_modules')
        .insert([{
          company_id: companyId,
          title,
          description,
          category: 'safety',
          difficulty_level: 'beginner',
          content: { slides } as any,
          quiz_questions: slides.filter(s => s.type === 'quiz').map(s => ({
            question: s.quizQuestion,
            options: s.quizOptions,
            answer: s.quizAnswer,
            explanation: s.quizExplanation
          })) as any,
          xp_reward: xpReward,
          duration_minutes: Math.ceil(slides.length * 2),
          is_active: true,
        }])
        .select('id')
        .single();

      if (moduleError) throw moduleError;

      // Calculate expires_at based on timing
      const expiresAt = new Date();
      if (deliveryTiming === 'scheduled' && scheduledDate) {
        const scheduled = new Date(`${scheduledDate}T${scheduledTime}:00`);
        expiresAt.setTime(scheduled.getTime());
      }
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Create quest template
      const { data: templateData, error: templateError } = await supabase
        .from('quest_templates')
        .insert([{
          company_id: companyId,
          title,
          description,
          quest_type: 'learning',
          intervention_type: 'micro_learning',
          xp_reward: xpReward,
          content: { module_id: moduleData.id, slides } as any,
          duration_hours: 168,
          is_active: true,
        }])
        .select('id')
        .single();

      if (templateError) throw templateError;

      // Create worker quests for each target
      const workerQuests = targetWorkerIds.map(workerId => ({
        worker_id: workerId,
        template_id: templateData.id,
        status: 'pending',
        expires_at: expiresAt.toISOString(),
        response_data: {},
      }));

      const { error: questsError } = await supabase
        .from('worker_quests')
        .insert(workerQuests);

      if (questsError) throw questsError;

      // Mark MDP suggestion as deployed if this came from a recommendation
      if (suggestionId) {
        await supabase
          .from('intervention_suggestions')
          .update({ status: 'deployed', updated_at: new Date().toISOString() })
          .eq('id', suggestionId);
      }

      const timingMessage = deliveryTiming === 'now' 
        ? 'and assigned' 
        : `scheduled for ${scheduledDate} ${scheduledTime}`;
      toast.success(`Learning module created ${timingMessage} to ${targetWorkerIds.length} workers!`);
      navigate('/admin?tab=intervention');
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish learning module');
    } finally {
      setLoading(false);
    }
  };

  const addSlide = (type: LearningSlide['type']) => {
    const newSlide: LearningSlide = {
      id: Date.now().toString(),
      type,
      title: '',
      content: '',
      quizOptions: type === 'quiz' ? ['', '', '', ''] : undefined,
      quizAnswer: type === 'quiz' ? 0 : undefined,
    };
    setSlides([...slides, newSlide]);
  };

  const updateSlide = (id: string, updates: Partial<LearningSlide>) => {
    setSlides(slides.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const removeSlide = (id: string) => {
    setSlides(slides.filter(s => s.id !== id));
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Wand2 className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Describe Your Training</h2>
        <p className="text-muted-foreground">Tell us what you want to teach. Our AI will generate a complete Duolingo-style learning module.</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <Textarea
          value={trainingDescription}
          onChange={(e) => setTrainingDescription(e.target.value)}
          placeholder="Example: Safety training about working at heights for construction workers. Cover proper harness usage, ladder safety, and fall prevention techniques."
          rows={6}
          className="text-base"
        />

        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
          <p className="text-sm font-medium">AI will generate:</p>
          <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Multiple content slides
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Interactive quizzes
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Explanations for answers
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              AI-generated images
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Who Should Take This Training?</h2>
        <p className="text-muted-foreground">Select the target audience for this learning module.</p>
      </div>

      <div className="max-w-xl mx-auto">
        <RadioGroup value={targetType} onValueChange={(v) => setTargetType(v as any)} className="space-y-3">
          <Label className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="all" />
            <Users className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">All Workers</p>
              <p className="text-sm text-muted-foreground">Everyone in the company</p>
            </div>
          </Label>
          <Label className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="team" />
            <Users className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="font-medium">Specific Team</p>
              {targetType === 'team' && (
                <Select value={targetTeam} onValueChange={setTargetTeam}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team} value={team}>{team}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </Label>
          <Label className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="cluster" />
            <Users className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="font-medium">Cluster</p>
              {targetType === 'cluster' && (
                <Select value={targetClusterId} onValueChange={setTargetClusterId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select cluster" />
                  </SelectTrigger>
                  <SelectContent>
                    {clusters.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.cluster_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </Label>
          <Label className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="individual" />
            <User className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="font-medium">Individual Worker</p>
              {targetType === 'individual' && (
                <Select value={targetWorkerId} onValueChange={setTargetWorkerId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select worker" />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.display_name} ({w.team})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </Label>
        </RadioGroup>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Edit & Customize</h2>
          <p className="text-muted-foreground">Review and customize the generated content</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={generateAllImages} disabled={generatingImages}>
            {generatingImages ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating...</>
            ) : (
              <><Image className="h-4 w-4 mr-1" /> Generate All Images</>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => addSlide('content')}>
            <Plus className="h-4 w-4 mr-1" /> Content
          </Button>
          <Button variant="outline" size="sm" onClick={() => addSlide('quiz')}>
            <Plus className="h-4 w-4 mr-1" /> Quiz
          </Button>
        </div>
      </div>

      {/* Title and Description */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Training Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter title" />
        </div>
        <div className="space-y-2">
          <Label>XP Reward</Label>
          <Input type="number" value={xpReward} onChange={(e) => setXpReward(parseInt(e.target.value) || 0)} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description" rows={2} />
      </div>

      {/* Slides */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold">Slides ({slides.length})</Label>
        <Reorder.Group axis="y" values={slides} onReorder={setSlides} className="space-y-4">
          {slides.map((slide, index) => (
            <Reorder.Item key={slide.id} value={slide}>
              <Card className="p-4 cursor-move">
                <div className="flex items-start gap-3">
                  <GripVertical className="h-5 w-5 text-muted-foreground mt-1 cursor-grab" />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant={slide.type === 'quiz' ? 'default' : 'secondary'}>
                        {index + 1}. {slide.type === 'content' && 'Content'}
                        {slide.type === 'quiz' && 'Quiz'}
                        {slide.type === 'spot_hazard' && 'Spot Hazard'}
                        {slide.type === 'reflection' && 'Reflection'}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => removeSlide(slide.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    <Input
                      value={slide.title}
                      onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
                      placeholder="Slide title"
                      className="font-medium"
                    />

                    {slide.type === 'content' && (
                      <>
                        <Textarea
                          value={slide.content}
                          onChange={(e) => updateSlide(slide.id, { content: e.target.value })}
                          placeholder="Content text..."
                          rows={3}
                        />
                        
                        {/* Image section */}
                        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm flex items-center gap-2">
                              <Image className="h-4 w-4" /> Slide Image
                            </Label>
                            {slide.imagePrompt && !slide.imageUrl && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => generateImageForSlide(slide.id)}
                                disabled={slide.generatingImage}
                              >
                                {slide.generatingImage ? (
                                  <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                                ) : (
                                  <><Sparkles className="h-3 w-3 mr-1" /> Generate</>
                                )}
                              </Button>
                            )}
                          </div>
                          
                          {slide.imageUrl ? (
                            <div className="relative">
                              <img 
                                src={slide.imageUrl} 
                                alt={slide.title} 
                                className="w-full max-h-48 object-cover rounded-lg"
                              />
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                className="absolute top-2 right-2"
                                onClick={() => updateSlide(slide.id, { imageUrl: undefined })}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Input
                              value={slide.imagePrompt || ''}
                              onChange={(e) => updateSlide(slide.id, { imagePrompt: e.target.value })}
                              placeholder="Describe the image you want (e.g., 'Worker wearing safety harness on scaffolding')"
                              className="text-sm"
                            />
                          )}
                        </div>
                      </>
                    )}

                    {slide.type === 'quiz' && (
                      <div className="space-y-3 bg-muted/50 p-3 rounded-lg">
                        <Input
                          value={slide.quizQuestion || ''}
                          onChange={(e) => updateSlide(slide.id, { quizQuestion: e.target.value })}
                          placeholder="Quiz question"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          {slide.quizOptions?.map((opt, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`answer-${slide.id}`}
                                checked={slide.quizAnswer === i}
                                onChange={() => updateSlide(slide.id, { quizAnswer: i })}
                              />
                              <Input
                                value={opt}
                                onChange={(e) => {
                                  const newOptions = [...(slide.quizOptions || [])];
                                  newOptions[i] = e.target.value;
                                  updateSlide(slide.id, { quizOptions: newOptions });
                                }}
                                placeholder={`Option ${i + 1}`}
                                className="flex-1"
                              />
                            </div>
                          ))}
                        </div>
                        <Input
                          value={slide.quizExplanation || ''}
                          onChange={(e) => updateSlide(slide.id, { quizExplanation: e.target.value })}
                          placeholder="Explanation for correct answer"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Reorder.Item>
          ))}
        </Reorder.Group>

        {slides.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed rounded-xl">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No slides yet. Add content or quiz slides.</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6 max-w-2xl mx-auto"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Clock className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Delivery Timing</h2>
        <p className="text-muted-foreground">When should this training be assigned?</p>
      </div>

      <RadioGroup value={deliveryTiming} onValueChange={(v) => setDeliveryTiming(v as 'now' | 'scheduled')}>
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              deliveryTiming === 'now' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
            }`}
            onClick={() => setDeliveryTiming('now')}
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="now" id="now" />
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-primary" />
                <div>
                  <Label htmlFor="now" className="font-semibold cursor-pointer">Assign Now</Label>
                  <p className="text-sm text-muted-foreground">Assign immediately</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              deliveryTiming === 'scheduled' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
            }`}
            onClick={() => setDeliveryTiming('scheduled')}
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="scheduled" id="scheduled" />
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-primary" />
                <div>
                  <Label htmlFor="scheduled" className="font-semibold cursor-pointer">Schedule</Label>
                  <p className="text-sm text-muted-foreground">Choose date & time</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </RadioGroup>

      {deliveryTiming === 'scheduled' && (
        <Card className="p-4 space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}
    </motion.div>
  );

  const canProceed = () => {
    if (step === 1) return trainingDescription.trim().length > 10;
    if (step === 2) {
      if (targetType === 'all') return true;
      if (targetType === 'team') return !!targetTeam;
      if (targetType === 'cluster') return !!targetClusterId;
      if (targetType === 'individual') return !!targetWorkerId;
    }
    if (step === 3) return title && slides.length > 0;
    if (step === 4) return deliveryTiming === 'now' || (deliveryTiming === 'scheduled' && scheduledDate);
    return false;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin?tab=intervention')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Create Micro Learning</h1>
              <p className="text-sm text-muted-foreground">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stepLabels.map((label, index) => (
              <div
                key={label}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > index + 1 ? 'bg-success text-success-foreground' :
                  step === index + 1 ? 'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground'
                }`}
              >
                {step > index + 1 ? <CheckCircle className="h-4 w-4" /> : index + 1}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 pb-24">
        <AnimatePresence mode="wait">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-xl border-t px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : navigate('/admin?tab=intervention')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step === 1 && (
            <Button onClick={() => setStep(2)} disabled={!canProceed()}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === 2 && (
            <Button onClick={handleGenerate} disabled={!canProceed() || generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          )}

          {step === 3 && (
            <Button onClick={() => setStep(4)} disabled={!canProceed()}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === 4 && (
            <Button onClick={handlePublish} disabled={!canProceed() || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {deliveryTiming === 'now' ? 'Publish Now' : 'Schedule Training'}
                </>
              )}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
